// src/admin/AdminProductMeta.jsx
// Manage images + descriptions for Sinalite products & categories
// Stored in MySQL document collection "productMeta" (doc id = supplier product id)
//                     collection "categoryMeta" (doc id = category name slug)

import { useEffect, useState, useRef } from "react";
import {
  collection, doc, getDoc, setDoc, getDocs, getDocsFromServer, writeBatch
} from "../services/mysqlStore";
import {
  ref, uploadBytesResumable, getDownloadURL, storage
} from "../services/mysqlStorage";
import { db } from "../services/mysqlStore";
import API_BASE from "../services/apiClient";
import { fetchPrice, fetchProductOptions } from "../services/sinaliteService";
import AdminCustomCategories from "./AdminCustomCategories";

// ── Icons ─────────────────────────────────────────────────────────────────────
const UploadIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>;
const SaveIcon  = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
const SearchIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const ImgIcon   = () => <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;

const CLOUD_BASE = API_BASE;

// ── Fetch all products from Sinalite API ──────────────────────────────────────
async function fetchSinaliteProducts() {
  const res = await fetch(`${CLOUD_BASE}/supplier/proxy?path=/product`);
  if (!res.ok) throw new Error("Failed to fetch products");
  const data = await res.json();
  return data.filter(p => p.enabled === 1);
}

// SinaLite's product list doesn't reliably include a photo, but some feeds do —
// check every common field name suppliers use before giving up.
function extractSupplierImageUrl(rawProduct) {
  const candidates = [
    rawProduct.image, rawProduct.imageUrl, rawProduct.image_url,
    rawProduct.thumbnail, rawProduct.thumbnailUrl, rawProduct.thumbnail_url,
    rawProduct.picture, rawProduct.pictureUrl, rawProduct.photo, rawProduct.photoUrl,
  ];
  const found = candidates.find(value => typeof value === "string" && /^https?:\/\//.test(value));
  return found || "";
}

// ── Upload image to the MySQL API storage service ───────────────────────────
function uploadImage(file, path, onProgress) {
  return new Promise((resolve, reject) => {
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);
    task.on("state_changed",
      snap => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      () => getDownloadURL(task.snapshot.ref).then(resolve).catch(reject)
    );
  });
}

// ── Single product/category editor row ───────────────────────────────────────
function PriceEstimate({ estimate, loading, onLoad }) {
  return (
    <div style={{ marginTop: 12, padding: 10, borderRadius: 7, background: "rgba(0,170,221,0.06)", border: "1px solid rgba(0,170,221,0.18)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
        <div><span style={priceLabelStyle}>Supplier cost</span><strong style={priceValueStyle}>{estimate ? `$${estimate.sinalitePrice.toFixed(2)}` : "—"}</strong></div>
        <div><span style={priceLabelStyle}>Website price</span><strong style={priceValueStyle}>{estimate ? `$${estimate.displayPrice.toFixed(2)}` : "—"}</strong></div>
        <div><span style={priceLabelStyle}>Profit / margin</span><strong style={priceValueStyle}>{estimate ? `$${(estimate.displayPrice - estimate.sinalitePrice).toFixed(2)} (${estimate.displayPrice ? ((estimate.displayPrice - estimate.sinalitePrice) / estimate.displayPrice * 100).toFixed(1) : "0.0"}%)` : "—"}</strong></div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", marginTop: 8 }}>
        <span style={{ color: "var(--adm-muted)", fontSize: 10 }}>Estimate for supplier's first available options.</span>
        <button type="button" onClick={onLoad} disabled={loading} className="adm-btn adm-btn-sm">{loading ? "Loading..." : estimate ? "Refresh" : "Load estimate"}</button>
      </div>
    </div>
  );
}

function MetaRow({ item, type, savedMeta, onSave, selected = false, onToggleSelect, estimate, estimateLoading, onLoadEstimate }) {
  const [images,      setImages]      = useState(savedMeta?.images || (savedMeta?.imageUrl ? [savedMeta.imageUrl] : []));
  const [activeImg,   setActiveImg]   = useState(0);
  const [description, setDescription] = useState(savedMeta?.description || "");
  const [markupPercent, setMarkupPercent] = useState(
    savedMeta?.markupPercent != null ? savedMeta.markupPercent : ""
  );
  const [uploading,   setUploading]   = useState(false);
  const [progress,    setProgress]    = useState(0);
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [showAllThumbs, setShowAllThumbs] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    setImages(savedMeta?.images || (savedMeta?.imageUrl ? [savedMeta.imageUrl] : []));
    setDescription(savedMeta?.description || "");
    setMarkupPercent(savedMeta?.markupPercent != null ? savedMeta.markupPercent : "");
    setActiveImg(0);
  }, [savedMeta]);

  async function handleFile(e) {
    const files = Array.from(e.target.files);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    setProgress(0);
    setUploadError("");
    try {
      const urls = await Promise.all(files.map((file, i) => {
        const path = type === "product"
          ? `product-images/${item.id}_${Date.now()}_${i}_${file.name}`
          : `category-images/${item.slug}_${Date.now()}_${i}_${file.name}`;
        return uploadImage(file, path, p => setProgress(Math.round((i / files.length * 100) + p / files.length)));
      }));
      setImages(prev => [...prev, ...urls]);
    } catch (error) {
      setUploadError(error.message || "Upload failed. Check the API and admin login.");
    }
    finally { setUploading(false); setProgress(0); }
  }

  function removeImage(i) {
    setImages(prev => {
      const next = prev.filter((_, idx) => idx !== i);
      setActiveImg(Math.min(activeImg, next.length - 1));
      return next;
    });
  }

  async function handleSave() {
    const markupValue = markupPercent === "" ? null : Number(markupPercent);
    if (markupValue !== null && (!Number.isFinite(markupValue) || markupValue < 0 || markupValue > 500)) {
      alert("Markup must be a number between 0 and 500%.");
      return;
    }
    setSaving(true);
    try {
      const colName = type === "product" ? "productMeta" : "categoryMeta";
      const docId   = type === "product" ? String(item.id) : item.slug;
      const imageUrl = images[0] || "";
      await setDoc(doc(db, colName, docId), {
        imageUrl, images, description,
        ...(type === "product" ? { productId: item.id, name: item.name, category: item.category, markupPercent: markupValue } : { category: item.name, markupPercent: markupValue }),
        updatedAt: new Date(),
      }, { merge: true });
      onSave(docId, { imageUrl, images, description, markupPercent: markupValue });
      // Clear cached meta so the public site shows the new image/description/margin immediately
      try { localStorage.removeItem("sinalite_meta_cache_v1"); } catch {}
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch { alert("Save failed"); }
    finally { setSaving(false); }
  }

  return (
    <div style={rowStyle}>
      {/* Images */}
      <div style={imgColStyle}>
        {/* Main preview */}
        <div style={imgBoxStyle} onClick={() => fileRef.current?.click()} title="Click to add images">
          {images.length > 0
            ? <img src={images[activeImg]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
            : <div style={{ color: "rgba(255,255,255,0.2)" }}><ImgIcon /></div>
          }
          <div style={imgOverlayStyle}><UploadIcon /></div>
        </div>
        {/* Thumbnails — capped to avoid a wall of tiny icons on products with many images */}
        {images.length > 1 && (
          <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap", maxWidth: 90 }}>
            {(showAllThumbs ? images : images.slice(0, 6)).map((url, i) => (
              <div key={i} style={{ position: "relative" }}>
                <img
                  src={url}
                  alt=""
                  onClick={() => setActiveImg(i)}
                  style={{ width: 26, height: 26, objectFit: "cover", borderRadius: 4, cursor: "pointer", border: activeImg === i ? "2px solid var(--cyan)" : "2px solid transparent" }}
                />
                <button
                  onClick={e => { e.stopPropagation(); removeImage(i); }}
                  style={{ position: "absolute", top: -5, right: -5, width: 14, height: 14, borderRadius: "50%", background: "#e91e8c", border: "none", color: "#fff", fontSize: 9, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                >×</button>
              </div>
            ))}
            {!showAllThumbs && images.length > 6 && (
              <button onClick={() => setShowAllThumbs(true)} style={{ width: 26, height: 26, borderRadius: 4, background: "rgba(255,255,255,0.08)", border: "none", color: "var(--adm-muted)", fontSize: 10, cursor: "pointer" }}>
                +{images.length - 6}
              </button>
            )}
          </div>
        )}
        {/* Single image remove */}
        {images.length === 1 && (
          <button onClick={() => setImages([])} style={{ marginTop: 4, fontSize: 10, background: "rgba(239,68,68,0.2)", border: "none", color: "#f87171", borderRadius: 4, padding: "2px 6px", cursor: "pointer" }}>
            Remove
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleFile} />
        {uploading && (
          <div style={{ marginTop: 6, width: 90 }}>
            <div style={{ height: 3, background: "rgba(255,255,255,0.1)", borderRadius: 2 }}>
              <div style={{ height: "100%", width: progress + "%", background: "var(--cyan)", borderRadius: 2, transition: "width 0.2s" }} />
            </div>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>Uploading...</span>
          </div>
        )}
        <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", marginTop: 4 }}>
          {images.length} image{images.length !== 1 ? "s" : ""}
        </span>
        {uploadError && <span style={{ color: "#f87171", fontSize: 11, lineHeight: 1.35, maxWidth: 150 }}>{uploadError}</span>}
      </div>

      {/* Info */}
      <div style={{ flex: 1 }}>
        <div style={{ marginBottom: 6 }}>
          {type === "product" && <input type="checkbox" checked={selected} onChange={() => onToggleSelect(item.id)} aria-label={`Select ${item.name}`} style={{ marginRight: 8 }} />}
          <strong style={{ color: "var(--adm-text)", fontSize: 14 }}>{item.name}</strong>
          {type === "product" && <span style={catTagStyle}>{item.category}</span>}
          {type === "product" && (
            <span style={{ fontSize: 11, color: "var(--adm-muted)", marginLeft: 8, fontFamily: "monospace" }}>
              ID: {item.id}
            </span>
          )}
        </div>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder={type === "product"
            ? "Enter product description (shown on product page)..."
            : "Enter category description (shown on categories section)..."}
          rows={3}
          style={textareaStyle}
        />
        {(
          <div style={{ marginTop: 10 }}>
            <label style={{ fontSize: 12, color: "var(--adm-muted)", fontWeight: 600, display: "block", marginBottom: 4 }}>
              {type === "product" ? "Product profit margin override" : "Category profit margin override"}
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <input
                type="number"
                min="0"
                max="500"
                step="0.1"
                value={markupPercent}
                onChange={e => setMarkupPercent(e.target.value)}
                style={{ width: 70, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, color: "var(--adm-text)", padding: "6px 8px", fontSize: 13, outline: "none" }}
              />
              <span style={{ fontSize: 12, color: "var(--adm-muted)" }}>% (leave blank to inherit)</span>
              <span style={{ fontSize: 11, color: "var(--adm-muted)", flexBasis: "100%" }}>
                {markupPercent === "" ? "Uses the category margin, or the global default if none is set." : `A $10.00 supplier cost sells for $${(10 * (1 + Number(markupPercent) / 100)).toFixed(2)}.`}
              </span>
            </div>
          </div>
        )}
        {type === "product" && <PriceEstimate estimate={estimate} loading={estimateLoading} onLoad={() => onLoadEstimate(item)} />}
      </div>

      {/* Save */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
        <button onClick={handleSave} disabled={saving || uploading} style={saveBtnStyle(saving || uploading)}>
          <SaveIcon /> {saving ? "Saving..." : saved ? "✓ Saved!" : "Save"}
        </button>
        {saved && <span style={{ color: "#4ade80", fontSize: 11, fontWeight: 700 }}>Saved ✓</span>}
      </div>
    </div>
  );
}

// ── Bulk Category Image — apply one image to every product in a category ──────
function BulkCategoryImage({ products, onApplied }) {
  const [category, setCategory]   = useState("");
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [applying, setApplying]   = useState(false);
  const [done, setDone]           = useState(0);
  const [msg, setMsg]             = useState("");
  const fileRef = useRef();

  const uniqueCats = [...new Set(products.map(p => p.category))].sort();
  const countInCat = category ? products.filter(p => p.category === category).length : 0;

  function handlePick(e) {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    e.target.value = "";
  }

  async function handleApply() {
    if (!category) { setMsg("error:Select a category first"); return; }
    if (!file) { setMsg("error:Upload an image first"); return; }

    setApplying(true);
    setDone(0);
    setMsg("");
    try {
      // 1. Upload the image once
      setUploading(true);
      const path = `product-images/bulk_${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}_${Date.now()}_${file.name}`;
      const imageUrl = await uploadImage(file, path, setProgress);
      setUploading(false);

      // 2. Apply to every product in this category via a single batched write
      const targetProducts = products.filter(p => p.category === category);
      const batch = writeBatch(db);
      targetProducts.forEach(p => {
        const ref = doc(db, "productMeta", String(p.id));
        batch.set(ref, {
          imageUrl,
          images: [imageUrl],
          productId: p.id,
          name: p.name,
          updatedAt: new Date(),
        }, { merge: true });
      });
      await batch.commit();

      // Clear cache so the public site reflects this immediately
      try { localStorage.removeItem("sinalite_meta_cache_v1"); } catch {}

      setDone(targetProducts.length);
      onApplied(category, imageUrl, targetProducts.map(p => String(p.id)));
      setMsg(`success:Applied to ${targetProducts.length} product${targetProducts.length !== 1 ? "s" : ""} in "${category}"`);
      setFile(null);
      setPreview("");
    } catch (err) {
      console.error(err);
      setMsg("error:Failed to apply image. Please try again.");
    } finally {
      setApplying(false);
      setUploading(false);
      setProgress(0);
    }
  }

  const isErr = msg.startsWith("error:");
  const msgText = msg.replace(/^(success|error):/, "");

  return (
    <div style={{ ...rowStyle, alignItems: "flex-start", background: "rgba(0,170,221,0.05)", border: "1px solid rgba(0,170,221,0.25)" }}>
      <div style={imgColStyle}>
        <div style={imgBoxStyle} onClick={() => fileRef.current?.click()} title="Click to choose an image">
          {preview
            ? <img src={preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
            : <div style={{ color: "rgba(255,255,255,0.2)" }}><ImgIcon /></div>
          }
          <div style={imgOverlayStyle}><UploadIcon /></div>
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handlePick} />
        {uploading && (
          <div style={{ marginTop: 6, width: 90 }}>
            <div style={{ height: 3, background: "rgba(255,255,255,0.1)", borderRadius: 2 }}>
              <div style={{ height: "100%", width: progress + "%", background: "var(--cyan)", borderRadius: 2, transition: "width 0.2s" }} />
            </div>
          </div>
        )}
      </div>

      <div style={{ flex: 1 }}>
        <strong style={{ color: "var(--adm-text)", fontSize: 14, display: "block", marginBottom: 8 }}>
          Bulk Apply — One Image to a Whole Category
        </strong>
        <p style={{ color: "var(--adm-muted)", fontSize: 12, marginBottom: 10, lineHeight: 1.6 }}>
          Pick a category and upload one image — it will be set as the image for every product in that category. Useful when you don't have individual photos for all 164 products.
        </p>
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          className="adm-select"
          style={{ ...selectStyle, width: "100%", marginBottom: 8 }}
        >
          <option value="">Select a category…</option>
          {uniqueCats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {category && (
          <p style={{ fontSize: 12, color: "var(--cyan)", marginBottom: 8 }}>
            This will update {countInCat} product{countInCat !== 1 ? "s" : ""} in "{category}"
          </p>
        )}
        {msg && (
          <div style={{ padding: "8px 12px", borderRadius: 6, fontSize: 12, marginBottom: 8, background: isErr ? "rgba(239,68,68,0.12)" : "rgba(74,222,128,0.12)", color: isErr ? "#f87171" : "#4ade80" }}>
            {msgText}
          </div>
        )}
      </div>

      <button
        onClick={handleApply}
        disabled={applying || !category || !file}
        style={saveBtnStyle(applying || !category || !file)}
      >
        <SaveIcon /> {applying ? "Applying..." : "Apply to All"}
      </button>
    </div>
  );
}

function BulkProductImages({ products, savedMeta, onUpdated }) {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");
  const inputRef = useRef();
  const matches = search.trim()
    ? products.filter(product => `${product.name} ${product.id} ${product.category}`.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8)
    : [];
  const selectedProduct = products.find(product => String(product.id) === selectedId);
  const existingImages = selectedProduct
    ? savedMeta[selectedId]?.images || (savedMeta[selectedId]?.imageUrl ? [savedMeta[selectedId].imageUrl] : [])
    : [];

  async function handleUpload() {
    if (!selectedProduct || files.length === 0) return;
    setUploading(true);
    setProgress(0);
    setMessage("");
    try {
      const uploadedUrls = [];
      for (const [index, file] of files.entries()) {
        const uploadPath = `product-images/${selectedProduct.id}_${Date.now()}_${index}_${file.name}`;
        const url = await uploadImage(file, uploadPath, value => setProgress(Math.round(((index + value / 100) / files.length) * 100)));
        uploadedUrls.push(url);
      }
      const images = [...existingImages, ...uploadedUrls];
      const nextMeta = {
        ...(savedMeta[selectedId] || {}),
        productId: selectedProduct.id,
        name: selectedProduct.name,
        category: selectedProduct.category,
        imageUrl: images[0] || "",
        images,
        updatedAt: new Date(),
      };
      await setDoc(doc(db, "productMeta", selectedId), nextMeta, { merge: true });
      try { localStorage.removeItem("sinalite_meta_cache_v1"); } catch {}
      onUpdated(selectedId, nextMeta);
      setFiles([]);
      setMessage(`Uploaded ${uploadedUrls.length} image${uploadedUrls.length === 1 ? "" : "s"} for ${selectedProduct.name}.`);
    } catch (error) {
      setMessage(`error:${error.message || "Image upload failed."}`);
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return <section className="adm-card" style={{ padding: 22 }}>
    <style>{".product-image-upload-grid { display:grid; grid-template-columns:minmax(240px,1fr) minmax(260px,1fr); gap:18px; align-items:start; } @media (max-width:760px) { .product-image-upload-grid { grid-template-columns:1fr; } }"}</style>
    <h3 style={{ color: "var(--adm-text)", margin: "0 0 8px" }}>Bulk Upload Images to One Product</h3>
    <p style={{ color: "var(--adm-muted)", fontSize: 13, margin: "0 0 16px" }}>Search for and select one product, then upload multiple images. This only updates that product, never a category.</p>
    <div className="product-image-upload-grid">
      <div>
        <label style={{ ...productMetaLabelStyle, marginBottom: 6 }}>Find product by name, ID, or category</label>
        <div style={searchWrapStyle}><SearchIcon /><input className="adm-search-input" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search products..." style={searchInputStyle} /></div>
        {matches.length > 0 && <div style={{ display: "grid", gap: 6, marginTop: 8, maxHeight: 240, overflowY: "auto" }}>{matches.map(product => <button key={product.id} type="button" onClick={() => { setSelectedId(String(product.id)); setSearch(product.name); setFiles([]); setMessage(""); }} style={{ ...galleryProductButton, textAlign: "left" }}><strong>{product.name}</strong><span style={{ color: "var(--adm-muted)", fontSize: 11 }}>ID: {product.id} · {product.category}</span></button>)}</div>}
        {selectedProduct && <div style={{ ...galleryProductButton, marginTop: 12 }}><strong>Selected: {selectedProduct.name}</strong><span style={{ color: "var(--adm-muted)", fontSize: 11 }}>Product ID: {selectedProduct.id} · {selectedProduct.category}</span><span style={{ color: "var(--adm-muted)", fontSize: 11 }}>{existingImages.length} existing image{existingImages.length === 1 ? "" : "s"}</span></div>}
      </div>
      <div>
        <label style={{ ...productMetaLabelStyle, marginBottom: 6 }}>Images to add</label>
        <input ref={inputRef} type="file" accept="image/*" multiple disabled={!selectedProduct || uploading} onChange={event => setFiles(Array.from(event.target.files || []))} />
        {files.length > 0 && <p style={{ color: "var(--adm-muted)", fontSize: 12, margin: "8px 0" }}>{files.length} image{files.length === 1 ? "" : "s"} selected: {files.map(file => file.name).join(", ")}</p>}
        {uploading && <div style={{ margin: "10px 0", color: "var(--adm-muted)", fontSize: 12 }}>Uploading {progress}%<div style={{ height: 4, background: "rgba(255,255,255,0.1)", marginTop: 5 }}><div style={{ height: "100%", width: `${progress}%`, background: "var(--cyan)" }} /></div></div>}
        <button type="button" className="adm-btn adm-btn-primary" disabled={!selectedProduct || !files.length || uploading} onClick={handleUpload} style={{ marginTop: 10 }}>{uploading ? "Uploading..." : "Upload to Selected Product"}</button>
        {message && <p style={{ color: message.startsWith("error:") ? "#f87171" : "#4ade80", fontSize: 12 }}>{message.replace(/^error:/, "")}</p>}
      </div>
    </div>
  </section>;
}

function ProductImageGallery({ products, savedMeta }) {
  const galleryImages = products.flatMap(product => {
    const meta = savedMeta[String(product.id)] || {};
    const images = meta.images || (meta.imageUrl ? [meta.imageUrl] : []);
    return images.map((url, index) => {
      let storageLocation = url;
      try { storageLocation = new URL(url, window.location.origin).pathname.replace(/^\/uploads\//, ""); } catch {}
      return { key: `${product.id}-${index}-${url}`, product, url, storageLocation };
    });
  });

  return <section className="adm-card" style={{ padding: 22 }}>
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}><div><h3 style={{ color: "var(--adm-text)", margin: 0 }}>Product Image Gallery</h3><p style={{ color: "var(--adm-muted)", fontSize: 12, margin: "5px 0 0" }}>Preview each image, its storage location, and the product it belongs to.</p></div><strong style={{ color: "var(--cyan)" }}>{galleryImages.length} images</strong></div>
    {galleryImages.length === 0 ? <p style={{ color: "var(--adm-muted)" }}>No product images have been uploaded.</p> : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 12 }}>{galleryImages.map(image => <article key={image.key} style={galleryCardStyle}><a href={image.url} target="_blank" rel="noopener noreferrer" title="Open full-size image"><img src={image.url} alt={image.product.name} style={{ width: "100%", height: 160, objectFit: "contain", background: "#fff" }} /></a><div style={{ padding: 10, display: "grid", gap: 5 }}><strong style={{ color: "var(--adm-text)", fontSize: 13 }}>{image.product.name}</strong><span style={{ color: "var(--adm-muted)", fontSize: 11 }}>Product ID: {image.product.id} · {image.product.category}</span><code style={{ color: "var(--cyan)", fontSize: 10, overflowWrap: "anywhere" }}>{image.storageLocation}</code></div></article>)}</div>}
  </section>;
}

// ── Same product/category, shown as one dense table row with every field the supplier gives us ──
function MetaListRow({ item, type, savedMeta, onSave, selected = false, onToggleSelect, estimate, estimateLoading, onLoadEstimate }) {
  const [description, setDescription] = useState(savedMeta?.description || "");
  const [markupPercent, setMarkupPercent] = useState(savedMeta?.markupPercent != null ? savedMeta.markupPercent : "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const images = savedMeta?.images || (savedMeta?.imageUrl ? [savedMeta.imageUrl] : []);

  useEffect(() => {
    setDescription(savedMeta?.description || "");
    setMarkupPercent(savedMeta?.markupPercent != null ? savedMeta.markupPercent : "");
  }, [savedMeta]);

  async function handleSave() {
    const markupValue = markupPercent === "" ? null : Number(markupPercent);
    if (markupValue !== null && (!Number.isFinite(markupValue) || markupValue < 0 || markupValue > 500)) {
      alert("Markup must be a number between 0 and 500%.");
      return;
    }
    setSaving(true);
    try {
      const colName = type === "product" ? "productMeta" : "categoryMeta";
      const docId = type === "product" ? String(item.id) : item.slug;
      await setDoc(doc(db, colName, docId), {
        imageUrl: images[0] || "", images, description,
        ...(type === "product" ? { productId: item.id, name: item.name, category: item.category, markupPercent: markupValue } : { category: item.name, markupPercent: markupValue }),
        updatedAt: new Date(),
      }, { merge: true });
      onSave(docId, { imageUrl: images[0] || "", images, description, markupPercent: markupValue });
      try { localStorage.removeItem("sinalite_meta_cache_v1"); } catch {}
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch { alert("Save failed"); }
    finally { setSaving(false); }
  }

  return (
    <tr style={listRowStyle}>
      <td style={listCellStyle}>
        {images.length > 0
          ? <img src={images[0]} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6 }} />
          : <div style={{ width: 40, height: 40, borderRadius: 6, background: "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.2)" }}><ImgIcon /></div>
        }
      </td>
      {type === "product" && <td style={listCellStyle}><input type="checkbox" checked={selected} onChange={() => onToggleSelect(item.id)} aria-label={`Select ${item.name}`} /></td>}
      <td style={{ ...listCellStyle, fontFamily: "monospace", color: "var(--adm-muted)" }}>{item.id ?? item.slug}</td>
      {type === "product" && <td style={{ ...listCellStyle, fontFamily: "monospace", color: "var(--adm-muted)" }}>{item.sku ?? "—"}</td>}
      <td style={{ ...listCellStyle, color: "var(--adm-text)", fontWeight: 600 }}>{item.name}</td>
      <td style={listCellStyle}>{type === "product" ? item.category : "—"}</td>
      {type === "product" && (
        <td style={listCellStyle}>
          <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, background: item.enabled === 1 ? "rgba(74,222,128,0.15)" : "rgba(239,68,68,0.15)", color: item.enabled === 1 ? "#4ade80" : "#f87171" }}>
            {item.enabled === 1 ? "Enabled" : "Disabled"}
          </span>
        </td>
      )}
      <td style={listCellStyle}>{images.length}</td>
      <td style={{ ...listCellStyle, minWidth: 220 }}>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} style={{ ...textareaStyle, minHeight: "unset", fontSize: 12 }} placeholder="Description..." />
      </td>
      {type === "product" && <td style={{ ...listCellStyle, minWidth: 190 }}>
        <PriceEstimate estimate={estimate} loading={estimateLoading} onLoad={() => onLoadEstimate(item)} />
      </td>}
      {(
        <td style={listCellStyle}>
          <input type="number" min="0" max="500" step="0.1" value={markupPercent} onChange={e => setMarkupPercent(e.target.value)} style={{ width: 60, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, color: "var(--adm-text)", padding: "4px 6px", fontSize: 12 }} />
          <span style={{ fontSize: 11, color: "var(--adm-muted)", marginLeft: 4 }}>%</span>
        </td>
      )}
      <td style={listCellStyle}>
        <button onClick={handleSave} disabled={saving} style={{ ...saveBtnStyle(saving), padding: "6px 10px", fontSize: 12 }}>
          {saving ? "..." : saved ? "✓" : "Save"}
        </button>
      </td>
    </tr>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function AdminProductMeta({ initialMode = "products" } = {}) {
  const [mode,        setMode]        = useState(initialMode); // products, categories, bulk, bulkProductImages, gallery, customCats
  const [products,    setProducts]    = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [savedMeta,   setSavedMeta]   = useState({});  // { docId: { imageUrl, description } }
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [filterCat,   setFilterCat]   = useState("all");
  const [syncing,     setSyncing]     = useState(false);
  const [fetchingPhotos, setFetchingPhotos] = useState(false);
  const [lastSynced,  setLastSynced]  = useState(null);
  const [syncMessage, setSyncMessage] = useState("");
  const [viewMode,    setViewMode]    = useState("grid"); // "grid" | "list"
  const [defaultMarkupInput, setDefaultMarkupInput] = useState("35");
  const [bulkMarkupInput, setBulkMarkupInput] = useState("");
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [pricingMessage, setPricingMessage] = useState("");
  const [priceEstimates, setPriceEstimates] = useState({});
  const [loadingPriceIds, setLoadingPriceIds] = useState([]);
  const [batchPriceLoading, setBatchPriceLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      // 1. Load Sinalite products
      const prods = await fetchSinaliteProducts();

      // 2. Build unique categories list
      const cats = [...new Set(prods.map(p => p.category))].sort().map(c => ({
        name: c,
        slug: c.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      }));

      // 3. Load saved metadata from MySQL — force server fetch so admin always sees the latest
      //    (avoids Firestore's local cache showing an old image after a recent save)
      const [prodSnap, catSnap, pricingSnap] = await Promise.all([
        getDocsFromServer(collection(db, "productMeta")),
        getDocsFromServer(collection(db, "categoryMeta")),
        getDoc(doc(db, "siteSettings", "pricing")),
      ]);

      const meta = {};
      prodSnap.docs.forEach(d => { meta[d.id] = d.data(); });
      catSnap.docs.forEach(d => { meta[d.id] = d.data(); });

      // Detect products the supplier added since we last had any metadata for them at all
      // (as opposed to ones that simply never got a photo uploaded).
      const newlyAdded = prods.filter(p => !meta[String(p.id)]);

      setProducts(prods);
      setCategories(cats);
      setSavedMeta(meta);
      setDefaultMarkupInput(String(pricingSnap.exists() ? pricingSnap.data().defaultMarkupPercent ?? 35 : 35));
      setLastSynced(new Date());
      return newlyAdded;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load().catch(err => console.error(err));
  }, []);

  async function handleRefreshFromSupplier() {
    setSyncing(true);
    setSyncMessage("");
    try {
      const newlyAdded = await load();
      setSyncMessage(newlyAdded.length
        ? `Synced with supplier — ${newlyAdded.length} new product${newlyAdded.length !== 1 ? "s" : ""} found, need a photo added.`
        : "Synced with supplier — no new products found.");
    } catch (err) {
      setSyncMessage(`error: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  }

  async function handleFetchPhotosFromSupplier() {
    setFetchingPhotos(true);
    setSyncMessage("");
    try {
      const missing = products.filter(p => !savedMeta[String(p.id)]?.imageUrl);
      let filled = 0;
      for (const p of missing) {
        const imageUrl = extractSupplierImageUrl(p);
        if (!imageUrl) continue;
        const docId = String(p.id);
        await setDoc(doc(db, "productMeta", docId), { ...(savedMeta[docId] || {}), productId: p.id, name: p.name, category: p.category, imageUrl }, { merge: true });
        setSavedMeta(prev => ({ ...prev, [docId]: { ...(prev[docId] || {}), productId: p.id, name: p.name, category: p.category, imageUrl } }));
        filled += 1;
      }
      setSyncMessage(filled
        ? `Fetched ${filled} photo${filled !== 1 ? "s" : ""} from the supplier. ${missing.length - filled} product${missing.length - filled !== 1 ? "s" : ""} still need a photo uploaded manually — the supplier doesn't provide one for those.`
        : "The supplier doesn't provide photos for any of the missing products — please upload them manually below.");
    } catch (err) {
      setSyncMessage(`error: ${err.message}`);
    } finally {
      setFetchingPhotos(false);
    }
  }

  function handleSave(docId, data) {
    setSavedMeta(prev => ({ ...prev, [docId]: { ...prev[docId], ...data } }));
  }

  function toggleProductSelection(productId) {
    const id = String(productId);
    setSelectedProductIds(current => current.includes(id)
      ? current.filter(selectedId => selectedId !== id)
      : [...current, id]);
  }

  function toggleVisibleProductSelection() {
    const visibleIds = filteredProducts.map(product => String(product.id));
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedProductIds.includes(id));
    setSelectedProductIds(current => allVisibleSelected
      ? current.filter(id => !visibleIds.includes(id))
      : [...new Set([...current, ...visibleIds])]);
  }

  function validateMarkup(value) {
    const parsed = value === "" ? null : Number(value);
    if (parsed === null || !Number.isFinite(parsed) || parsed < 0 || parsed > 500) {
      throw new Error("Markup must be a number between 0 and 500%.");
    }
    return parsed;
  }

  async function handleSaveDefaultMarkup() {
    let markupPercent;
    try {
      markupPercent = validateMarkup(defaultMarkupInput);
    } catch (error) {
      setPricingMessage(`error:${error.message}`);
      return;
    }
    setPricingSaving(true);
    setPricingMessage("");
    try {
      await setDoc(doc(db, "siteSettings", "pricing"), {
        defaultMarkupPercent: markupPercent,
        updatedAt: new Date(),
      });
      setPricingMessage("success:Default markup saved. Products without overrides now use this value.");
    } catch (error) {
      setPricingMessage(`error:${error.message || "Could not save the default markup."}`);
    } finally {
      setPricingSaving(false);
    }
  }

  async function handleApplyMarkupToProducts(productList, markupPercent) {
    if (!productList.length) {
      setPricingMessage("error:Select at least one product.");
      return;
    }
    setPricingSaving(true);
    setPricingMessage("");
    try {
      const batch = writeBatch(db);
      productList.forEach(product => {
        batch.update(doc(db, "productMeta", String(product.id)), {
          productId: product.id,
          name: product.name,
          category: product.category,
          markupPercent,
          updatedAt: new Date(),
        });
      });
      await batch.commit();
      setSavedMeta(current => {
        const next = { ...current };
        productList.forEach(product => {
          const id = String(product.id);
          next[id] = { ...next[id], productId: product.id, name: product.name, category: product.category, markupPercent, updatedAt: new Date() };
        });
        return next;
      });
      setSelectedProductIds([]);
      setPricingMessage(`success:Markup updated for ${productList.length} product${productList.length === 1 ? "" : "s"}.`);
    } catch (error) {
      setPricingMessage(`error:${error.message || "Could not update product markups."}`);
    } finally {
      setPricingSaving(false);
    }
  }

  async function handleApplyMarkupToAll() {
    let markupPercent;
    try {
      markupPercent = validateMarkup(defaultMarkupInput);
    } catch (error) {
      setPricingMessage(`error:${error.message}`);
      return;
    }
    if (!window.confirm(`Apply ${markupPercent}% to all ${products.length} products? This overrides category and per-product markups.`)) return;
    await handleApplyMarkupToProducts(products, markupPercent);
  }

  async function handleApplyMarkupToSelected() {
    let markupPercent;
    try {
      markupPercent = validateMarkup(bulkMarkupInput);
    } catch (error) {
      setPricingMessage(`error:${error.message}`);
      return;
    }
    const selectedProducts = products.filter(product => selectedProductIds.includes(String(product.id)));
    await handleApplyMarkupToProducts(selectedProducts, markupPercent);
  }

  async function handleResetSelectedMarkup() {
    if (!selectedProductIds.length) {
      setPricingMessage("error:Select at least one product.");
      return;
    }
    setPricingSaving(true);
    setPricingMessage("");
    try {
      const batch = writeBatch(db);
      selectedProductIds.forEach(id => batch.update(doc(db, "productMeta", id), { markupPercent: null, updatedAt: new Date() }));
      await batch.commit();
      setSavedMeta(current => {
        const next = { ...current };
        selectedProductIds.forEach(id => { next[id] = { ...next[id], markupPercent: null }; });
        return next;
      });
      const count = selectedProductIds.length;
      setSelectedProductIds([]);
      setPricingMessage(`success:${count} product${count === 1 ? "" : "s"} now inherit the category or global markup.`);
    } catch (error) {
      setPricingMessage(`error:${error.message || "Could not clear the selected product overrides."}`);
    } finally {
      setPricingSaving(false);
    }
  }

  async function handleResetAllProductMarkups() {
    if (!window.confirm(`Clear product-specific overrides for all ${products.length} products? Products will use their category override, or the global default.`)) return;
    setPricingSaving(true);
    setPricingMessage("");
    try {
      const batch = writeBatch(db);
      products.forEach(product => batch.update(doc(db, "productMeta", String(product.id)), { markupPercent: null, updatedAt: new Date() }));
      await batch.commit();
      setSavedMeta(current => {
        const next = { ...current };
        products.forEach(product => {
          const id = String(product.id);
          next[id] = { ...next[id], markupPercent: null };
        });
        return next;
      });
      setPricingMessage(`success:Cleared product-specific overrides for ${products.length} products. Category rules and the global default are now active.`);
    } catch (error) {
      setPricingMessage(`error:${error.message || "Could not clear all product overrides."}`);
    } finally {
      setPricingSaving(false);
    }
  }

  async function loadPriceEstimate(product) {
    const productId = String(product.id);
    setLoadingPriceIds(current => current.includes(productId) ? current : [...current, productId]);
    try {
      const { grouped } = await fetchProductOptions(product.id);
      const selectedIds = Object.values(grouped).map(options => options[0]?.id).filter(id => id != null);
      if (!selectedIds.length) throw new Error("Supplier did not provide any default product options.");
      const estimate = await fetchPrice(product.id, selectedIds, product.category);
      setPriceEstimates(current => ({ ...current, [productId]: estimate }));
      return true;
    } catch (error) {
      setPricingMessage(`error:${product.name}: ${error.message || "Could not load supplier pricing."}`);
      return false;
    } finally {
      setLoadingPriceIds(current => current.filter(id => id !== productId));
    }
  }

  async function loadVisiblePriceEstimates() {
    setBatchPriceLoading(true);
    setPricingMessage("");
    let failures = 0;
    for (let index = 0; index < filteredProducts.length; index += 4) {
      const results = await Promise.all(filteredProducts.slice(index, index + 4).map(loadPriceEstimate));
      failures += results.filter(success => !success).length;
    }
    setPricingMessage(failures
      ? `error:Loaded prices with ${failures} supplier pricing failure${failures === 1 ? "" : "s"}.`
      : `success:Loaded supplier cost, website price, and estimated profit for ${filteredProducts.length} products.`);
    setBatchPriceLoading(false);
  }

  // Apply one image to every productId in a category at once (after BulkCategoryImage finishes)
  function handleBulkApplied(category, imageUrl, productIds) {
    setSavedMeta(prev => {
      const next = { ...prev };
      productIds.forEach(id => {
        next[id] = { ...next[id], imageUrl, images: [imageUrl] };
      });
      return next;
    });
  }

  // Filter products
  const filteredProducts = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchCat    = filterCat === "all" || p.category === filterCat;
    return matchSearch && matchCat;
  });

  const filteredCategories = categories.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const uniqueCats = [...new Set(products.map(p => p.category))].sort();

  // Stats
  const productsWithImage = products.filter(p => savedMeta[String(p.id)]?.imageUrl).length;
  const catsWithImage     = categories.filter(c => savedMeta[c.slug]?.imageUrl).length;

  if (loading) return (
    <div style={{ padding: 60, textAlign: "center" }}>
      <p style={{ color: "rgba(255,255,255,0.4)" }}>Loading products from Sinalite...</p>
    </div>
  );

  return (
    <div>
      <style>{`
        .adm-search-input::placeholder { color: rgba(255,255,255,0.35) !important; }
        .adm-search-input { color: #fff !important; background: transparent !important; }
        .adm-select { background: #1a1a2e !important; color: #fff !important; colorScheme: dark; }
        .adm-select option { background: #1a1a2e !important; color: #fff !important; }
      `}</style>
      {/* Mode Toggle */}
      <div style={{ display: "flex", gap: 10, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={() => { setMode("products"); setSearch(""); setFilterCat("all"); }} style={modeBtn(mode === "products")}>
          Products ({products.length})
        </button>
        <button onClick={() => { setMode("categories"); setSearch(""); }} style={modeBtn(mode === "categories")}>
          Categories ({categories.length})
        </button>
        <button onClick={() => setMode("bulk")} style={modeBtn(mode === "bulk")}>
          Bulk Category Image
        </button>
        <button onClick={() => { setMode("bulkProductImages"); setSearch(""); }} style={modeBtn(mode === "bulkProductImages")}>
          Bulk Product Images
        </button>
        <button onClick={() => { setMode("gallery"); setSearch(""); }} style={modeBtn(mode === "gallery")}>
          Product Image Gallery
        </button>
        <button onClick={() => setMode("customCats")} style={modeBtn(mode === "customCats")}>
          Custom Categories
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={handleFetchPhotosFromSupplier} disabled={fetchingPhotos || syncing} className="adm-btn">
          {fetchingPhotos ? "Fetching photos..." : "\ud83d\uddbc\ufe0f Fetch photos from supplier"}
        </button>
        <button onClick={handleRefreshFromSupplier} disabled={syncing || fetchingPhotos} className="adm-btn adm-btn-primary">
          {syncing ? "Refreshing..." : "🔄 Refresh from Supplier"}
        </button>
      </div>
      <section className="adm-card" style={{ padding: 18, marginBottom: 16 }}>
        <h3 style={{ color: "var(--adm-text)", margin: "0 0 6px" }}>Selling price markup</h3>
        <p style={{ color: "var(--adm-muted)", fontSize: 12, lineHeight: 1.5, margin: "0 0 14px" }}>
          Prices are calculated from the supplier cost. Precedence is product override, category override, then the global default.
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <label htmlFor="default-markup" style={{ color: "var(--adm-text)", fontSize: 13, fontWeight: 600 }}>Global default</label>
          <input id="default-markup" type="number" min="0" max="500" step="0.1" value={defaultMarkupInput} onChange={event => setDefaultMarkupInput(event.target.value)} style={{ width: 90, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, color: "var(--adm-text)", padding: "7px 8px" }} />
          <span style={{ color: "var(--adm-muted)", fontSize: 12 }}>%</span>
          <button onClick={handleSaveDefaultMarkup} disabled={pricingSaving} className="adm-btn adm-btn-primary">{pricingSaving ? "Saving..." : "Save default"}</button>
          <button onClick={handleApplyMarkupToAll} disabled={pricingSaving || !products.length} className="adm-btn">Apply to all {products.length} products</button>
          <button onClick={handleResetAllProductMarkups} disabled={pricingSaving || !products.length} className="adm-btn">Reset all to category/default</button>
        </div>
        {mode === "products" && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 14, paddingTop: 14 }}>
            <strong style={{ color: "var(--adm-text)", fontSize: 13 }}>{selectedProductIds.length} selected</strong>
            <input aria-label="Selected products markup percentage" type="number" min="0" max="500" step="0.1" value={bulkMarkupInput} onChange={event => setBulkMarkupInput(event.target.value)} placeholder="Markup %" style={{ width: 105, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, color: "var(--adm-text)", padding: "7px 8px" }} />
            <button onClick={handleApplyMarkupToSelected} disabled={pricingSaving || !selectedProductIds.length} className="adm-btn">Apply to selected</button>
            <button onClick={handleResetSelectedMarkup} disabled={pricingSaving || !selectedProductIds.length} className="adm-btn">Reset selected to inherited</button>
          </div>
        )}
        {pricingMessage && <p role="status" style={{ color: pricingMessage.startsWith("error:") ? "#f87171" : "#4ade80", fontSize: 12, margin: "12px 0 0" }}>{pricingMessage.replace(/^(success|error):/, "")}</p>}
      </section>
      {(lastSynced || syncMessage) && (
        <div style={{ marginBottom: 20, fontSize: 12 }}>
          {lastSynced && <span style={{ color: "var(--adm-muted)" }}>Last synced: {lastSynced.toLocaleTimeString()}</span>}
          {syncMessage && (
            <span style={{ marginLeft: 12, color: syncMessage.startsWith("error:") ? "#f87171" : "#4ade80" }}>
              {syncMessage.replace(/^error:\s*/, "")}
            </span>
          )}
        </div>
      )}

      {/* Stats bar */}
      {(mode === "products" || mode === "categories") && (
        <div style={statsBarStyle}>
          {mode === "products" ? (
            <>
              <span>Total: <strong style={{ color: "var(--adm-text)" }}>{products.length}</strong></span>
              <span>Images uploaded: <strong style={{ color: "#4ade80" }}>{productsWithImage}</strong></span>
              <span>Missing images: <strong style={{ color: "#f5c200" }}>{products.length - productsWithImage}</strong></span>
            </>
          ) : (
            <>
              <span>Total: <strong style={{ color: "var(--adm-text)" }}>{categories.length}</strong></span>
              <span>Images uploaded: <strong style={{ color: "#4ade80" }}>{catsWithImage}</strong></span>
              <span>Missing images: <strong style={{ color: "#f5c200" }}>{categories.length - catsWithImage}</strong></span>
            </>
          )}
        </div>
      )}

      {/* Filters */}
      {(mode === "products" || mode === "categories") && (
        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={searchWrapStyle}>
            <SearchIcon />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={mode === "products" ? "Search products..." : "Search categories..."}
              className="adm-search-input" style={{ ...searchInputStyle, colorScheme: "dark" }}
            />
          </div>
          {mode === "products" && (
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={selectStyle} className="adm-select">
              <option value="all">All Categories</option>
              {uniqueCats.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {mode === "products" && <button onClick={toggleVisibleProductSelection} className="adm-btn">{filteredProducts.length > 0 && filteredProducts.every(product => selectedProductIds.includes(String(product.id))) ? "Unselect visible" : "Select visible"}</button>}
          {mode === "products" && <button onClick={loadVisiblePriceEstimates} disabled={batchPriceLoading || !filteredProducts.length} className="adm-btn adm-btn-primary">{batchPriceLoading ? "Loading supplier prices..." : "Load prices for visible products"}</button>}
          <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
            <button onClick={() => setViewMode("grid")} style={modeBtn(viewMode === "grid")} title="Grid view — image, description, profit margin">▦ Grid</button>
            <button onClick={() => setViewMode("list")} style={modeBtn(viewMode === "list")} title="List view — every field the supplier gives us">☰ List</button>
          </div>
        </div>
      )}

      {/* List */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {mode === "customCats" && (
          <AdminCustomCategories products={products} />
        )}
        {mode === "bulk" && (
          <BulkCategoryImage products={products} onApplied={handleBulkApplied} />
        )}
        {mode === "bulkProductImages" && (
          <BulkProductImages products={products} savedMeta={savedMeta} onUpdated={handleSave} />
        )}
        {mode === "gallery" && (
          <ProductImageGallery products={products} savedMeta={savedMeta} />
        )}
        {viewMode === "grid" && (mode === "products" || mode === "categories") && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))", gap: 12 }}>
            {mode === "products" && filteredProducts.map(p => (
              <MetaRow
                key={p.id}
                item={p}
                type="product"
                savedMeta={savedMeta[String(p.id)]}
                onSave={handleSave}
                selected={selectedProductIds.includes(String(p.id))}
                onToggleSelect={toggleProductSelection}
                estimate={priceEstimates[String(p.id)]}
                estimateLoading={loadingPriceIds.includes(String(p.id))}
                onLoadEstimate={loadPriceEstimate}
              />
            ))}
            {mode === "categories" && filteredCategories.map(c => (
              <MetaRow
                key={c.slug}
                item={c}
                type="category"
                savedMeta={savedMeta[c.slug]}
                onSave={handleSave}
              />
            ))}
          </div>
        )}
        {viewMode === "list" && (mode === "products" || mode === "categories") && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={listHeadStyle}>Image</th>
                  {mode === "products" && <th style={listHeadStyle}>Select</th>}
                  <th style={listHeadStyle}>ID</th>
                  {mode === "products" && <th style={listHeadStyle}>SKU</th>}
                  <th style={listHeadStyle}>Name</th>
                  <th style={listHeadStyle}>Category</th>
                  {mode === "products" && <th style={listHeadStyle}>Status</th>}
                  <th style={listHeadStyle}>Images</th>
                  <th style={listHeadStyle}>Description</th>
                  {mode === "products" && <th style={listHeadStyle}>Price estimate</th>}
                  <th style={listHeadStyle}>Margin override</th>
                  <th style={listHeadStyle}></th>
                </tr>
              </thead>
              <tbody>
                {mode === "products" && filteredProducts.map(p => (
                  <MetaListRow key={p.id} item={p} type="product" savedMeta={savedMeta[String(p.id)]} onSave={handleSave} selected={selectedProductIds.includes(String(p.id))} onToggleSelect={toggleProductSelection} estimate={priceEstimates[String(p.id)]} estimateLoading={loadingPriceIds.includes(String(p.id))} onLoadEstimate={loadPriceEstimate} />
                ))}
                {mode === "categories" && filteredCategories.map(c => (
                  <MetaListRow key={c.slug} item={c} type="category" savedMeta={savedMeta[c.slug]} onSave={handleSave} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {mode === "products" && filteredProducts.length === 0 && (
          <p style={{ color: "rgba(255,255,255,0.3)", textAlign: "center", padding: 40 }}>No products found.</p>
        )}
        {mode === "categories" && filteredCategories.length === 0 && (
          <p style={{ color: "rgba(255,255,255,0.3)", textAlign: "center", padding: 40 }}>No categories found.</p>
        )}
      </div>
    </div>
  );
}


// ── Styles ────────────────────────────────────────────────────────────────────
const rowStyle = {
  display: "flex", gap: 16, alignItems: "flex-start",
  background: "var(--adm-products-grid-bg, var(--adm-card))",
  border: "1px solid rgba(255,255,255,0.07)",
  borderRadius: 12, padding: 16,
};
const listHeadStyle = {
  textAlign: "left", padding: "8px 10px", fontSize: 11, fontWeight: 700,
  color: "var(--adm-muted)", textTransform: "uppercase", letterSpacing: 0.5,
  borderBottom: "1px solid rgba(255,255,255,0.1)", whiteSpace: "nowrap",
};
const listRowStyle = { borderBottom: "1px solid rgba(255,255,255,0.06)", background: "var(--adm-products-list-bg, transparent)" };
const listCellStyle = { padding: "8px 10px", fontSize: 13, color: "var(--adm-text)", verticalAlign: "middle" };
const imgColStyle = { display: "flex", flexDirection: "column", gap: 4 };
const imgBoxStyle = {
  width: 90, height: 80, borderRadius: 8,
  background: "rgba(255,255,255,0.06)",
  border: "1.5px dashed rgba(255,255,255,0.15)",
  display: "flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer", position: "relative", overflow: "hidden",
  flexShrink: 0,
};
const imgOverlayStyle = {
  position: "absolute", inset: 0,
  background: "rgba(0,0,0,0.5)",
  display: "flex", alignItems: "center", justifyContent: "center",
  color: "#fff", opacity: 0,
  transition: "opacity 0.2s",
};
const textareaStyle = {
  width: "100%", background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8,
  color: "var(--adm-text)", padding: "10px 12px", fontSize: 13,
  fontFamily: "var(--font-main)", resize: "vertical",
  outline: "none", lineHeight: 1.6, boxSizing: "border-box",
};
const catTagStyle = {
  background: "rgba(0,170,221,0.15)", color: "var(--cyan)",
  padding: "2px 8px", borderRadius: 20, fontSize: 11,
  fontWeight: 600, marginLeft: 8,
};
function saveBtnStyle(disabled) {
  return {
    display: "flex", alignItems: "center", gap: 6,
    background: disabled ? "rgba(255,255,255,0.06)" : "var(--cyan)",
    color: disabled ? "rgba(255,255,255,0.3)" : "#fff",
    border: "none", borderRadius: 8, padding: "8px 14px",
    fontSize: 13, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
    whiteSpace: "nowrap", fontFamily: "var(--font-main)",
  };
}
const statsBarStyle = {
  display: "flex", gap: 20, background: "var(--adm-card)",
  border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10,
  padding: "12px 16px", marginBottom: 16, fontSize: 13,
  color: "var(--adm-muted)", flexWrap: "wrap",
};
const searchWrapStyle = {
  display: "flex", alignItems: "center", gap: 8,
  background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 8, padding: "0 12px", flex: 1, minWidth: 200,
  color: "var(--adm-muted)",
};
const searchInputStyle = {
  background: "transparent", border: "none", color: "var(--adm-text)",
  padding: "9px 0", fontSize: 13, outline: "none",
  fontFamily: "var(--font-main)", width: "100%",
  caretColor: "var(--adm-text)",
};
const productMetaLabelStyle = { display: "block", color: "var(--adm-muted)", fontSize: 12, fontWeight: 600 };
const priceLabelStyle = { display: "block", color: "var(--adm-muted)", fontSize: 10, lineHeight: 1.4 };
const priceValueStyle = { display: "block", color: "var(--adm-text)", fontSize: 12, overflowWrap: "anywhere" };
const galleryProductButton = { display: "grid", gap: 4, width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 7, padding: 10, color: "var(--adm-text)", cursor: "pointer", boxSizing: "border-box" };
const galleryCardStyle = { minWidth: 0, overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, background: "rgba(255,255,255,0.03)" };
const selectStyle = {
  background: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 8, color: "var(--adm-text)", padding: "9px 14px", fontSize: 13,
  fontFamily: "var(--font-main)", cursor: "pointer", outline: "none",
  colorScheme: "dark",
};
function modeBtn(active) {
  return {
    background: active ? "var(--cyan)" : "rgba(255,255,255,0.06)",
    color: active ? "#fff" : "var(--adm-muted)",
    border: "1px solid " + (active ? "var(--cyan)" : "rgba(255,255,255,0.12)"),
    padding: "9px 18px", borderRadius: 8, fontSize: 13,
    fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-main)",
  };
}
